/**
 * Team Measure Chart Component
 *
 * Leadership dashboard: vertical column bar chart showing team-wide
 * performance per measure, averaged across all filtered providers.
 * Replaces horizontal MeasureComparison with a vertical layout.
 */

import { useMemo } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from 'recharts'
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/components/tailwindadmin/components/ui/chart'
import { getMeasureDisplayName } from '@/lib/equip-utils'
import {
  getLeadershipMeasureColumnsFromSchedule,
  LEADERSHIP_MEASURE_ORDER,
} from '@/lib/measureScheduleSelectors'
import { useMeasureThresholdMap, type MeasureThresholdMap } from '@/hooks/equip/useMeasureThresholdMap'
import type { LeadershipProviderRow } from '@/lib/equip-types'

const chartConfig = {
  rate: { label: 'Rate' },
} satisfies ChartConfig

const PASS_COLOR = 'var(--equip-pass-light)'
const FAIL_COLOR = 'var(--equip-fail-light)'

/**
 * Color palette for threshold reference lines. Ordered so the most common
 * threshold (~90%) picks up the first slate tone, with distinct hues for
 * tiered quarters (e.g. BCS 45/65) that show up as additional lines.
 */
export const THRESHOLD_PALETTE = [
  '#94a3b8', // slate-400
  '#a78bfa', // violet-400
  '#38bdf8', // sky-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
] as const

interface TeamMeasureChartProps {
  providers: LeadershipProviderRow[]
  isLoading: boolean
  year: number
  quarter: number
}

/**
 * Find the threshold pair for a measure by checking each distinct role
 * present in the provider set. On a mixed-role team (e.g., PCP + Psych Med),
 * some measures only have a schedule row for one role — using the first
 * provider's role blindly would silently fall back to the module defaults.
 * Returns the first matching pair, or undefined if no role has a row.
 */
function resolveMeasurePair(
  measureName: string,
  thresholdMap: MeasureThresholdMap,
  providers: LeadershipProviderRow[]
) {
  const seen = new Set<string>()
  for (const p of providers) {
    const role = p.bonusEligibleType
    if (!role || seen.has(role)) continue
    seen.add(role)
    const pair = thresholdMap.get(measureName, role)
    if (pair) return pair
  }
  // No provider carried a usable role (e.g. the senior-leadership region chart
  // feeds ADCOE rows as virtual providers with bonusEligibleType=null). Outcome
  // thresholds are uniform across provider types within a measure, so fall back
  // to the first active schedule row for this measure rather than the module
  // default of 89/90% — which would misreport A1C/BCS/CRCS (44/45% in Q2).
  for (const item of thresholdMap.items) {
    if (item.isActive && item.measureName === measureName) {
      return { threshold: item.threshold, thresholdDisplay: item.thresholdDisplay }
    }
  }
  return undefined
}

/**
 * Returns the display threshold string for a measure from the config-driven
 * threshold map. Falls back to 90% (25% for ED_SendOut) when the lookup misses.
 */
function getMeasureThreshold(
  measureName: string,
  thresholdMap: MeasureThresholdMap,
  providers: LeadershipProviderRow[]
): string {
  const pair = resolveMeasurePair(measureName, thresholdMap, providers)
  if (!pair) {
    if (measureName === 'ED_SendOut') return '25% (lower is better)'
    return '90%'
  }
  const display = Math.round(pair.thresholdDisplay * 100)
  return measureName === 'ED_SendOut' ? `${display}% (lower is better)` : `${display}%`
}

/**
 * Custom Recharts tooltip — rendered only when a bar is hovered.
 * Shows measure name, average rate, and the pass threshold for that measure.
 */
interface TooltipEntry {
  value?: number
  payload?: { name: string; rate: number; passed: boolean }
}

interface MeasureTooltipProps {
  active?: boolean
  payload?: TooltipEntry[]
  thresholdMap: MeasureThresholdMap
  providers: LeadershipProviderRow[]
}

function MeasureTooltip({ active, payload, thresholdMap, providers }: MeasureTooltipProps) {
  if (!active || !payload || payload.length === 0) return null

  const entry = payload[0]
  const rawData = entry.payload
  if (!rawData) return null
  const measureName = rawData.name
  const rate = entry.value ?? 0
  const threshold = getMeasureThreshold(measureName, thresholdMap, providers)

  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2 shadow-md text-xs">
      <p className="font-semibold text-foreground mb-1">{getMeasureDisplayName(measureName)}</p>
      <p className="text-muted-foreground">
        Avg: <span className="font-medium text-foreground">{rate.toFixed(1)}%</span>
      </p>
      <p className="text-muted-foreground">
        Threshold: <span className="font-medium text-foreground">{threshold}</span>
      </p>
    </div>
  )
}

/**
 * Custom XAxis tick that wraps long measure display names to two lines
 * rather than rotating them. Lines are split at the last space before
 * the midpoint of the string.
 *
 * Uses a loose prop type because Recharts passes many additional properties
 * (index, angle, etc.) to custom tick components.
 */
export function MeasureTick(props: Record<string, unknown>) {
  const { x, y, payload } = props as { x?: number; y?: number; payload?: { value: string } }
  const label = getMeasureDisplayName(payload?.value ?? '')
  const maxLineLen = 10

  // Split into two lines if the label is longer than maxLineLen chars
  let line1 = label
  let line2 = ''
  if (label.length > maxLineLen) {
    // Find the last space at or before the midpoint
    const mid = Math.ceil(label.length / 2)
    const splitIdx = label.lastIndexOf(' ', mid)
    if (splitIdx > 0) {
      line1 = label.slice(0, splitIdx)
      line2 = label.slice(splitIdx + 1)
    }
  }

  const px = x ?? 0
  const py = y ?? 0

  return (
    <g transform={`translate(${px},${py})`}>
      <text
        x={0}
        y={0}
        dy={20}
        textAnchor="middle"
        fill="var(--muted-foreground)"
        fontSize={11}
      >
        <tspan x={0} dy={0}>{line1}</tspan>
        {line2 && <tspan x={0} dy={14}>{line2}</tspan>}
      </text>
    </g>
  )
}

/**
 * Small pill-shaped label drawn at the right end of a threshold reference
 * line. Renders inside the plot area (not clipped by the card padding) and
 * includes a native SVG `<title>` so hovering reveals which measures share
 * that threshold and whether higher or lower is passing.
 *
 * Recharts passes `viewBox` for ReferenceLine labels; we derive geometry
 * from it rather than relying on position presets that get clipped.
 */
export interface ThresholdLabelProps {
  viewBox?: { x?: number; y?: number; width?: number; height?: number }
  value: number
  color: string
  measures: string[]
  isEdSendout: boolean
}

export function ThresholdLabel({ viewBox, value, color, measures, isEdSendout }: ThresholdLabelProps) {
  const plotX = viewBox?.x ?? 0
  const plotWidth = viewBox?.width ?? 0
  const y = viewBox?.y ?? 0
  const text = `${Math.round(value)}%`
  const padX = 5
  const charW = 6
  const rectW = text.length * charW + padX * 2
  const rectH = 14
  const rectX = plotX + plotWidth - rectW - 2
  const rectY = y - rectH / 2

  const direction = isEdSendout ? 'below' : 'at or above'
  const measureList = measures.length > 3
    ? `${measures.slice(0, 3).join(', ')}, +${measures.length - 3} more`
    : measures.join(', ')
  const title = `${text} — pass threshold (${direction}) for ${measureList}`

  return (
    <g style={{ pointerEvents: 'auto' }}>
      <title>{title}</title>
      {/* Invisible hover target covering the full line for easier discovery */}
      <rect
        x={plotX}
        y={y - 6}
        width={plotWidth}
        height={12}
        fill="transparent"
      />
      <rect
        x={rectX}
        y={rectY}
        width={rectW}
        height={rectH}
        rx={rectH / 2}
        ry={rectH / 2}
        fill={color}
        fillOpacity={0.12}
        stroke={color}
        strokeOpacity={0.45}
        strokeWidth={1}
      />
      <text
        x={rectX + rectW / 2}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fontWeight={500}
        fill={color}
        style={{ letterSpacing: '0.01em' }}
      >
        {text}
      </text>
    </g>
  )
}

export function TeamMeasureChart({ providers, isLoading, year, quarter }: TeamMeasureChartProps) {
  const thresholdMap = useMeasureThresholdMap(year, quarter)

  const measureData = useMemo(() => {
    if (providers.length === 0) return []

    // Use the same quarter-aware column set as the heatmap grid, driven by
    // `och.equip_config_measure_schedule`. When the schedule hasn't loaded
    // yet, fall back to the canonical order intersected with measures that
    // actually have data so the chart doesn't flash empty.
    const fromSchedule = getLeadershipMeasureColumnsFromSchedule(
      thresholdMap.items,
      providers,
      quarter,
    )
    // fromSchedule is empty (not null) when no provider carries a usable role —
    // e.g. the senior-leadership region chart feeds ADCOE rows as virtual
    // providers with bonusEligibleType=null. Treat that like the schedule not
    // having loaded and fall back to the measures actually present, so the
    // chart renders instead of showing "No measure data available".
    const measureNames: string[] = fromSchedule && fromSchedule.length > 0
      ? fromSchedule
      : (() => {
          const present = new Set<string>()
          providers.forEach(p => Object.keys(p.measures).forEach(n => present.add(n)))
          return LEADERSHIP_MEASURE_ORDER.filter(m => present.has(m))
        })()

    return measureNames
      .map((measureName) => {
        // Average performance across providers who have data for this measure
        // (providers where the measure doesn't apply return undefined, filtered out)
        const performances = providers
          .map((p) => p.measures[measureName]?.performance)
          .filter((perf): perf is number => perf !== null && perf !== undefined)

        // No providers have data for this measure — exclude the column entirely
        if (performances.length === 0) return null

        const avg = performances.reduce((sum, p) => sum + p, 0) / performances.length

        // Backend returns 0-1 scale; convert to 0-100 for display
        const pct = avg * 100
        const isEdSendout = measureName === 'ED_SendOut'

        // Config-driven operational threshold (pct, 0-100). Mixed-role teams
        // may have schedule rows under only one of the visible roles, so try
        // each distinct role until a row is found. Fallbacks mirror the
        // pre-refactor hardcoded values so behavior is identical until the
        // config table is fully seeded.
        const pair = resolveMeasurePair(measureName, thresholdMap, providers)
        const opThreshold = pair
          ? pair.threshold * 100
          : isEdSendout
            ? 25
            : 89

        const passed = isEdSendout ? pct <= opThreshold : pct >= opThreshold

        return {
          name: measureName,
          rate: Math.round(pct * 10) / 10,
          passed,
          thresholdDisplay: pair ? pair.thresholdDisplay * 100 : isEdSendout ? 25 : 90,
        }
      })
      .filter((entry): entry is { name: string; rate: number; passed: boolean; thresholdDisplay: number } => entry !== null)
  }, [providers, quarter, thresholdMap])

  if (isLoading) {
    return <Skeleton className="h-[280px] w-full rounded-[var(--radius)]" />
  }

  if (measureData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Team Measure Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground">No measure data available</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Team Measure Performance</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Override ChartContainer's hardcoded aspect-video + h-72 with a fixed height
            so the bar chart fills the container properly at the correct Y scale. */}
        <ChartContainer config={chartConfig} className="h-[260px] aspect-auto">
          <BarChart
            data={measureData}
            barSize={75}
            margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
          >
            <CartesianGrid vertical={false} className="stroke-muted" />
            <XAxis
              dataKey="name"
              tick={(props) => <MeasureTick {...props} />}
              tickLine={false}
              axisLine={false}
              tickMargin={4}
              interval={0}
              height={44}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              tickLine={false}
              axisLine={false}
              width={36}
            />
            {/* Hover-only tooltip — Recharts Tooltip is hover-only by default.
                Cast to unknown to pass the extra `quarter` prop through. */}
            <ChartTooltip
              cursor={false}
              content={({ active, payload }) => (
                <MeasureTooltip
                  active={active}
                  payload={payload as TooltipEntry[] | undefined}
                  thresholdMap={thresholdMap}
                  providers={providers}
                />
              )}
            />
            {/* One dashed reference line per unique display threshold, each in a
                distinct muted hue so tiered quarters (e.g. BCS 45/65) are easy to
                tell apart. Labels render as small pills INSIDE the plot area to
                avoid being clipped by the card padding, and include a native
                `<title>` so hovering shows which measures map to each threshold. */}
            {(() => {
              const uniq = Array.from(new Set(measureData.map(m => m.thresholdDisplay))).sort(
                (a, b) => a - b,
              )
              return uniq.map((t, idx) => {
                const color = THRESHOLD_PALETTE[idx % THRESHOLD_PALETTE.length]
                const group = measureData.filter(m => m.thresholdDisplay === t)
                const measureLabels = group.map(m => getMeasureDisplayName(m.name))
                const isEdSendout = group.every(m => m.name === 'ED_SendOut')
                return (
                  <ReferenceLine
                    key={t}
                    y={t}
                    stroke={color}
                    strokeOpacity={0.55}
                    strokeDasharray="4 3"
                    strokeWidth={1.25}
                    ifOverflow="extendDomain"
                    label={(labelProps: { viewBox?: ThresholdLabelProps['viewBox'] }) => (
                      <ThresholdLabel
                        viewBox={labelProps.viewBox}
                        value={t}
                        color={color}
                        measures={measureLabels}
                        isEdSendout={isEdSendout}
                      />
                    )}
                  />
                )
              })
            })()}
            <Bar dataKey="rate" radius={8}>
              {measureData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.passed ? PASS_COLOR : FAIL_COLOR} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
